// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  databaseAircraftLabel,
} from "./flight-source.js";
import {
  secondaryLegacySampleAircraftTailNumber,
  secondarySampleAircraftTailNumber,
  tertiaryLegacySampleAircraftTailNumber,
  tertiarySampleAircraftTailNumber,
} from "../core/config.js";

export const aircraftProfilesLocalStorageKey = "aeroInsightAircraftProfiles";

export const AIRCRAFT_TYPE_LABELS = {
  single: "Single",
  multi: "Multi",
  multi_centerline: "Multi Centerline",
};

export function profileValue(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (Number.isFinite(value)) return String(value);
  return null;
}

export function loadLocalAircraftProfiles({
  storage = globalThis.localStorage,
  storageKey = aircraftProfilesLocalStorageKey,
} = {}) {
  try {
    const raw = storage?.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveLocalAircraftProfiles(profiles, {
  storage = globalThis.localStorage,
  storageKey = aircraftProfilesLocalStorageKey,
} = {}) {
  try {
    storage?.setItem(storageKey, JSON.stringify(profiles ?? {}));
    return true;
  } catch {
    return false;
  }
}

export function aircraftDisplayLabel(value) {
  const text = String(value ?? "").trim();
  const tailNumber = text.match(/\bN[0-9A-Z]{1,5}\b/i)?.[0];
  if (!tailNumber) return text;
  if (tailNumber.toUpperCase() === secondaryLegacySampleAircraftTailNumber) return secondarySampleAircraftTailNumber;
  if (tailNumber.toUpperCase() === tertiaryLegacySampleAircraftTailNumber) return tertiarySampleAircraftTailNumber;
  return tailNumber.toUpperCase();
}

export function aircraftPhotoFallbackLabel(value) {
  const tail = aircraftDisplayLabel(value);
  if (!tail) return "AC";
  return tail.replace(/[^A-Z0-9]/gi, "").slice(0, 6) || "AC";
}

export function aircraftPhotoDataUrl(profile) {
  return profileValue(profile?.photo?.dataUrl)
    ?? profileValue(profile?.imageDataUrl)
    ?? null;
}

// Display source for <img src>: the server photo URL when the profile came from
// the database summary (photos are no longer inlined there), else the legacy
// inline data URL.
export function aircraftPhotoSource(profile) {
  return profileValue(profile?.photo?.url) ?? aircraftPhotoDataUrl(profile);
}

export function databaseAircraftPhotoDataUrl(aircraft) {
  const metadata = aircraft?.profile_metadata && typeof aircraft.profile_metadata === "object" ? aircraft.profile_metadata : {};
  return profileValue(metadata.photoDataUrl ?? metadata.photo?.dataUrl);
}

export function inferredAircraftType(profile) {
  const engineCount = Number(profile?.engineLayout?.count);
  const aircraft = profile?.aircraft ?? {};
  const modelText = `${profileValue(aircraft.make) ?? ""} ${profileValue(aircraft.model) ?? ""}`.trim().toLowerCase();
  if (/cessna\s*337|skymaster|push[-\s]?pull/.test(modelText)) return "multi_centerline";
  if (Number.isFinite(engineCount) && engineCount > 1) return "multi";
  if (Number.isFinite(engineCount) && engineCount === 1) return "single";
  return null;
}

export function normalizedAircraftType(value, profile = null) {
  const raw = profileValue(value)?.toLowerCase() ?? "";
  const normalized = raw.replace(/[_\s]+/g, "-");
  const inferredType = inferredAircraftType(profile);
  if (inferredType === "multi_centerline") return "multi_centerline";
  if (!raw) return inferredAircraftType(profile);
  if (normalized === "single" || normalized === "single-engine-piston") return "single";
  if (normalized === "multi" || normalized === "multi-engine-piston" || normalized === "twin" || normalized === "multi-engine") return "multi";
  if (normalized === "multi-centerline" || normalized === "centerline" || normalized === "centerline-thrust") return "multi_centerline";
  if (raw.includes("337") || raw.includes("skymaster") || raw.includes("push-pull") || raw.includes("push pull")) return "multi_centerline";
  return inferredAircraftType(profile);
}

export function aircraftTypeLabel(profile) {
  return AIRCRAFT_TYPE_LABELS[normalizedAircraftType(profile?.aircraft?.type, profile)] ?? null;
}

// Join a make and model without repeating the make when the model already
// carries it ("Piper" + "Piper PA-12 Super Cruiser" -> "Piper PA-12 Super
// Cruiser", "Garmin" + "Garmin G3X" -> "Garmin G3X"). Returns whichever single
// value is present when the other is blank, or null when both are.
export function joinMakeModel(make, model) {
  const makeText = profileValue(make);
  const modelText = profileValue(model);
  if (makeText && modelText) {
    const lowerMake = makeText.toLowerCase();
    const lowerModel = modelText.toLowerCase();
    if (lowerModel === lowerMake || lowerModel.startsWith(`${lowerMake} `)) return modelText;
    return `${makeText} ${modelText}`;
  }
  return makeText ?? modelText ?? null;
}

export function aircraftMakeModelLabel(profile) {
  return joinMakeModel(profile?.aircraft?.make, profile?.aircraft?.model);
}

export function engineLabel(profile) {
  const engine = profile?.engine ?? {};
  return profileValue([profileValue(engine.make), profileValue(engine.model)].filter(Boolean).join(" "))
    ?? profileValue(engine.model)
    ?? null;
}

export function engineCountForProfile(profile) {
  const count = Number(profile?.engineLayout?.count ?? profile?.engine?.count);
  return Number.isFinite(count) && count > 0 ? Math.max(1, Math.trunc(count)) : 1;
}

export function propellerEngineLabel(index, count, type = "") {
  if (count <= 1) return "Propeller";
  if (type === "multi_centerline") return index === 0 ? "Front Propeller" : index === 1 ? "Rear Propeller" : `Engine ${index + 1} Propeller`;
  return index === 0 ? "Left Propeller" : index === 1 ? "Right Propeller" : `Engine ${index + 1} Propeller`;
}

function positiveNumberOrNull(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

export function normalizedPropellerEntries(profile, count = engineCountForProfile(profile)) {
  const engines = Array.isArray(profile?.engine?.engines) ? profile.engine.engines : [];
  const propellers = Array.isArray(profile?.propellers) ? profile.propellers : [];
  const singlePropeller = profileValue(profile?.engine?.propeller ?? profile?.propeller);
  const type = normalizedAircraftType(profile?.aircraft?.type, profile) ?? "";
  return Array.from({ length: count }, (_, index) => {
    const targetEngineId = `engine${index + 1}`;
    const indexedEngine = engines[index] && typeof engines[index] === "object" ? engines[index] : {};
    const indexedPropeller = propellers[index] && typeof propellers[index] === "object" ? propellers[index] : {};
    const engine = engines.find((entry) => entry && typeof entry === "object" && entry.id === targetEngineId)
      ?? (indexedEngine.id && indexedEngine.id !== targetEngineId ? {} : indexedEngine);
    const propeller = propellers.find((entry) => entry && typeof entry === "object" && entry.engineId === targetEngineId)
      ?? (indexedPropeller.engineId && indexedPropeller.engineId !== targetEngineId ? {} : indexedPropeller);
    const value = profileValue(engine.propeller ?? propeller.value ?? propeller.propeller ?? (index === 0 ? singlePropeller : null)) ?? "";
    const make = profileValue(engine.propellerMake ?? propeller.make ?? propeller.propMake ?? propeller.manufacturer ?? propeller.propellerMake);
    return {
      engineId: profileValue(engine.id ?? propeller.engineId) ?? targetEngineId,
      label: profileValue(engine.label ?? propeller.label)?.replace(/\s+Engine$/i, " Propeller") ?? propellerEngineLabel(index, count, type),
      ...(make ? { make } : {}),
      value,
      ratedHorsepower: positiveNumberOrNull(engine.ratedHorsepower),
      takeoffFuelFlowGph: positiveNumberOrNull(engine.takeoffFuelFlowGph),
    };
  });
}

export function propellerLabel(profile) {
  const entries = normalizedPropellerEntries(profile).filter((entry) => entry.value);
  if (!entries.length) return null;
  if (entries.length === 1) return entries[0].value;
  return entries.map((entry) => `${entry.label.replace(/\s+Propeller$/i, "")}: ${entry.value}`).join(" · ");
}

export function propellerTypeLabel(profile) {
  const value = profileValue(profile?.propellerType ?? profile?.engine?.propellerType);
  if (value === "fixed_pitch") return "Fixed Pitch";
  if (value === "constant_speed") return "Constant Speed";
  return null;
}

export function fuelLabel(profile) {
  return profileValue(profile?.fuel?.primary);
}

export function monitorLabel(profile, fallbackMonitor) {
  const make = profileValue(profile?.monitor?.make);
  const model = profileValue(profile?.monitor?.model);
  const makeModel = joinMakeModel(make, model);
  return profileValue(profile?.monitor?.type)
    ?? profileValue(makeModel)
    ?? model
    ?? profileValue(profile?.monitor?.installations?.at(-1)?.model)
    ?? profileValue(fallbackMonitor)
    ?? "Unknown";
}

export function databaseAircraftToProfile(aircraft) {
  const metadata = aircraft?.profile_metadata && typeof aircraft.profile_metadata === "object" ? aircraft.profile_metadata : {};
  const tailNumber = aircraft?.tail_number_normalized ?? aircraft?.tail_number ?? "";
  const engineCount = Number.parseInt(String(metadata.engineCount ?? ""), 10);
  const normalizedEngineCount = Number.isFinite(engineCount) && engineCount > 0 ? engineCount : null;
  const metadataLayout = metadata.engineLayout && typeof metadata.engineLayout === "object" ? metadata.engineLayout : {};
  const aircraftType = normalizedAircraftType(metadata.aircraftType, {
    aircraft: {
      make: aircraft?.make,
      model: aircraft?.model,
    },
    engineLayout: normalizedEngineCount ? { count: normalizedEngineCount } : undefined,
  });
  const propellers = Array.isArray(metadata.propellers)
    ? metadata.propellers.map((entry, index) => {
        const make = profileValue(entry?.make ?? entry?.propMake ?? entry?.manufacturer ?? entry?.propellerMake);
        return {
          engineId: profileValue(entry?.engineId) ?? `engine${index + 1}`,
          label: profileValue(entry?.label) ?? propellerEngineLabel(index, normalizedEngineCount ?? 1, aircraftType ?? ""),
          ...(make ? { make } : {}),
          value: profileValue(entry?.value ?? entry?.propeller) ?? "",
          ratedHorsepower: positiveNumberOrNull(entry?.ratedHorsepower),
          takeoffFuelFlowGph: positiveNumberOrNull(entry?.takeoffFuelFlowGph),
        };
      })
    : [];
  const monitorModel = profileValue(aircraft?.monitor_model) ?? profileValue(metadata.monitorModel);
  const fuelPrimary = profileValue(metadata.fuelPrimary);
  const propellerType = profileValue(metadata.propellerType);
  const fuelSystem = profileValue(metadata.engineFuelSystem ?? metadata.engineDetails?.fuelSystem);
  const engineFamily = profileValue(metadata.engineFamily ?? metadata.engineDetails?.engineFamily);
  const engineCooling = profileValue(metadata.engineCooling ?? metadata.engineDetails?.engineCooling ?? metadata.engineDetails?.coolingType);
  const engineControl = profileValue(metadata.engineControl ?? metadata.engineDetails?.engineControl ?? metadata.engineDetails?.controlType);
  const photoDataUrl = databaseAircraftPhotoDataUrl(aircraft);
  const photoUrl = profileValue(metadata.photoUrl);
  return {
    databaseId: aircraft?.id ?? null,
    ...(aircraft?.plan_access && typeof aircraft.plan_access === "object" ? { planAccess: aircraft.plan_access } : {}),
    ...(aircraft?.feature_access && typeof aircraft.feature_access === "object" ? { featureAccess: aircraft.feature_access } : {}),
    tailNumber,
    profileStatus: "database",
    aircraft: {
      make: profileValue(aircraft?.make),
      model: profileValue(aircraft?.model),
      type: profileValue(metadata.aircraftType) ?? aircraftType,
    },
    engine: {
      make: profileValue(aircraft?.engine_make),
      model: profileValue(aircraft?.engine_model),
      propeller: propellers[0]?.value || null,
      propellerType,
      engines: propellers.map((entry) => ({
        id: entry.engineId,
        label: entry.label.replace(/\s+Propeller$/i, " Engine"),
        propeller: entry.value || null,
        ratedHorsepower: entry.ratedHorsepower ?? null,
        takeoffFuelFlowGph: entry.takeoffFuelFlowGph ?? null,
      })),
      cylinders: null,
      aspiration: null,
      fuelSystem,
      engineFamily,
      engineCooling,
      engineControl,
    },
    propellers,
    propellerType,
    engineLayout: normalizedEngineCount
      ? {
          ...metadataLayout,
          count: normalizedEngineCount,
          ...(aircraftType === "multi_centerline" && !profileValue(metadataLayout.labelMode ?? metadataLayout.engineLabelMode) ? { labelMode: "front-rear" } : {}),
          engineIds: Array.isArray(metadataLayout.engineIds) ? metadataLayout.engineIds : Array.from({ length: normalizedEngineCount }, (_, index) => `engine${index + 1}`),
          cylindersPerEngine: Array.isArray(metadataLayout.cylindersPerEngine) ? metadataLayout.cylindersPerEngine : [],
          egtGroups: Array.isArray(metadataLayout.egtGroups) ? metadataLayout.egtGroups : [],
          chtGroups: Array.isArray(metadataLayout.chtGroups) ? metadataLayout.chtGroups : [],
        }
      : undefined,
    fuel: {
      primary: fuelPrimary,
      approved: fuelPrimary ? [fuelPrimary] : [],
    },
    monitor: {
      separateEngines: metadata.cgrTwin?.enabled === true,
      cgrTwin: metadata.cgrTwin ?? null,
      type: profileValue(aircraft?.monitor_type),
      make: profileValue(aircraft?.monitor_make),
      model: monitorModel,
      installations: monitorModel ? [{ model: monitorModel }] : [],
    },
    turbo: metadata.turbo && typeof metadata.turbo === "object"
      ? {
          isTurbo: typeof metadata.turbo.isTurbo === "boolean"
            ? metadata.turbo.isTurbo
            : [metadata.turbo.maxTitF, metadata.turbo.maxMapInHg].some((value) => Number.isFinite(Number(value))),
          maxTitF: Number.isFinite(Number(metadata.turbo.maxTitF)) ? Number(metadata.turbo.maxTitF) : null,
          maxMapInHg: Number.isFinite(Number(metadata.turbo.maxMapInHg)) ? Number(metadata.turbo.maxMapInHg) : null,
        }
      : undefined,
    operatingLimits: metadata.operatingLimits && typeof metadata.operatingLimits === "object"
      ? {
          chtMax: Number(metadata.operatingLimits.chtMax) > 0 ? Number(metadata.operatingLimits.chtMax) : null,
          oilTempMax: Number(metadata.operatingLimits.oilTempMax) > 0 ? Number(metadata.operatingLimits.oilTempMax) : null,
          oilPressureMin: Number(metadata.operatingLimits.oilPressureMin) > 0 ? Number(metadata.operatingLimits.oilPressureMin) : null,
          oilPressureMax: Number(metadata.operatingLimits.oilPressureMax) > 0 ? Number(metadata.operatingLimits.oilPressureMax) : null,
          ...(metadata.operatingLimits.oilPressure && typeof metadata.operatingLimits.oilPressure === "object"
            ? { oilPressure: metadata.operatingLimits.oilPressure } : {}),
          ...(metadata.operatingLimits.fuelPressure && typeof metadata.operatingLimits.fuelPressure === "object" && !Array.isArray(metadata.operatingLimits.fuelPressure)
            ? { fuelPressure: metadata.operatingLimits.fuelPressure } : {}),
        }
      : undefined,
    photo: photoUrl
      ? {
          url: photoUrl,
          storage: "db-photo-endpoint",
        }
      : photoDataUrl
      ? {
          dataUrl: photoDataUrl,
          storage: "db-inline",
        }
      : undefined,
    notes: "Loaded from the local aircraft database.",
  };
}

export function databaseAircraftProfilesMap(records = []) {
  return Object.fromEntries(
    records
      .map((aircraft) => {
        const profile = databaseAircraftToProfile(aircraft);
        return profile.tailNumber ? [profile.tailNumber, profile] : null;
      })
      .filter(Boolean),
  );
}

export function orderedDatabaseAircraftLabels(records = []) {
  return records.map((aircraft) => databaseAircraftLabel(aircraft)).filter(Boolean);
}

export function aircraftFleetSortIndex(aircraft, {
  databaseAircraftRecords = [],
  localAircraftProfiles = {},
} = {}) {
  const label = String(aircraft ?? "");
  if (!label) return Number.POSITIVE_INFINITY;
  const databaseIndex = orderedDatabaseAircraftLabels(databaseAircraftRecords).findIndex((item) => item === label);
  if (databaseIndex >= 0) return databaseIndex;
  const storedOrder = Number(localAircraftProfiles?.[label]?.displayOrder ?? localAircraftProfiles?.[label]?.display_order);
  return Number.isFinite(storedOrder) ? storedOrder : Number.POSITIVE_INFINITY;
}
